<template>
  <span>
    <v-tabs v-model="activeGroupId" bg-color="cyan-darken-3" show-arrows>
      <v-tab
        v-for="group in menuGroups"
        draggable="false"
        :key="'tab-' + group"
        :value="group"
        :to="`/module-settings/${group}`"
        slider-color="teal-lighten-3"
      >
        {{ group }}
      </v-tab>
      <v-spacer />
      <TuneDialog />
    </v-tabs>
    <v-expand-transition>
      <v-tabs
        v-if="menuSubgroups"
        :key="subgroup_key"
        v-model="activeSubgroupId"
        bg-color="teal-darken-3"
        slider-color="teal-lighten-3"
        color="#FFF"
        show-arrows
      >
        <v-tab
          v-for="subgroup in menuSubgroups"
          draggable="false"
          :key="'tab-subgroup-' + subgroup"
          :value="subgroup"
          :to="`/module-settings/${activeGroupId}/${subgroup}`"
        >
          {{ subgroup }}
        </v-tab>
      </v-tabs>
    </v-expand-transition>
    <v-container :key="container_key">
      <v-row>
        <v-expand-x-transition>
          <v-col v-if="variableConfigItems.length > 0">
            <v-expand-transition>
              <v-card>
                <v-card-title class="d-flex">
                  <v-btn
                    icon
                    variant="text"
                    :disabled="isDisabled"
                    :loading="loadingVariables"
                    @click="reloadVariables"
                  >
                    <v-icon>refresh</v-icon>
                  </v-btn>
                  <v-sheet class="align-self-center">Variables</v-sheet>
                </v-card-title>
                <v-card-text>
                  <ConfigInput
                    v-for="item in variableConfigItems"
                    :key="item['mbus address'] + '-list-item'"
                    show-buttons
                    :parameter="item"
                    :significance="5"
                    modbus-reload
                  />
                </v-card-text>
              </v-card>
            </v-expand-transition>
          </v-col>
        </v-expand-x-transition>
        <v-expand-x-transition>
          <v-col v-if="booleanConfigItems.length > 0">
            <v-expand-transition>
              <v-card>
                <v-card-title class="d-flex">
                  <v-btn
                    icon
                    variant="text"
                    :disabled="isDisabled"
                    :loading="loadingToggles"
                    @click="reloadToggles"
                  >
                    <v-icon>refresh</v-icon>
                  </v-btn>
                  <v-sheet class="align-self-center">Toggles</v-sheet>
                </v-card-title>
                <v-card-text>
                  <ConfigInput
                    v-for="item in booleanConfigItems"
                    :key="item['mbus address'] + '-list-item'"
                    show-buttons
                    :parameter="item"
                    :significance="5"
                    modbus-reload
                  />
                </v-card-text>
              </v-card>
            </v-expand-transition>
          </v-col>
        </v-expand-x-transition>
      </v-row>
    </v-container>
  </span>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import ConfigInput from "@/components/ConfigInput.vue";
import TuneDialog from "@/components/TuneDialog.vue";
import { useUtils } from "@/composables/useUtils";
import { ParamField } from "../enums/ParamField";
import { PortState } from "@/enums/PortState";
import { ModbusParam } from "@/types/ModbusParam";
import { useModbusStore } from "@/stores/useModbusStore";

const modbusStore = useModbusStore();
const route = useRoute();
const { isNullOrUndefined } = useUtils();

const base_subgroup_key = "tab-subgroup-";
const subgroup_key = ref(base_subgroup_key);
const container_key = ref("container_" + new Date().getTime());

const activeGroupId = ref("");
const activeSubgroupId = ref("");

const menuGroups = computed(() => modbusStore.groups);
const menuSubgroups = computed(() => {
  if (!activeGroupId.value || !(activeGroupId.value in modbusStore.subgroups)) {
    return;
  } else {
    return modbusStore.subgroups[activeGroupId.value];
  }
});

const paramsItems = computed<ModbusParam[]>(() => {
  return modbusStore.params.filter(
    (param: ModbusParam) =>
      !isNullOrUndefined(param.name) &&
      param.name !== "0" &&
      modbusStore.isParamUserReadable(param),
  );
});

const variableConfigItems = computed<ModbusParam[]>(() => {
  if (activeSubgroupId.value === "") {
    const items = paramsItems.value.filter(
      (item) =>
        item.group === activeGroupId.value &&
        item.input_type !== "checkbox" &&
        item.input_type !== "dropdown" &&
        item.input_type !== "switch",
    );
    return items.filter(
      (item) =>
        item.type === "int" ||
        item.type === "float" ||
        item.type === "uint16_t" ||
        item.type === "uint32_t" ||
        (item.type === "char2" && item.name.indexOf("[0:1]") > 0),
    );
  } else {
    const items = paramsItems.value.filter(
      (item) =>
        item.group === activeGroupId.value &&
        item.sub_group === activeSubgroupId.value &&
        item.input_type !== "checkbox" &&
        item.input_type !== "dropdown" &&
        item.input_type !== "switch",
    );
    return items.filter(
      (item) =>
        item.type === "int" ||
        item.type === "float" ||
        item.type === "uint16_t" ||
        item.type === "uint32_t" ||
        (item.type === "char2" && item.name.indexOf("[0:1]") > 0),
    );
  }
});

const booleanConfigItems = computed<ModbusParam[]>(() => {
  if (activeSubgroupId.value === "") {
    return paramsItems.value.filter(
      (item) =>
        item.group === activeGroupId.value &&
        (item.input_type === "checkbox" ||
          item.input_type === "dropdown" ||
          item.input_type === "switch"),
    );
  } else {
    return paramsItems.value.filter(
      (item) =>
        item.group === activeGroupId.value &&
        item.sub_group === activeSubgroupId.value &&
        (item.input_type === "checkbox" ||
          item.input_type === "dropdown" ||
          item.input_type === "switch"),
    );
  }
});

const loadingVariables = computed(() => {
  let res = false;
  variableConfigItems.value.forEach((item) => {
    res =
      res ||
      modbusStore.modbusAddrReadBusy[item[ParamField.mbusAddress]] ||
      modbusStore.modbusAddrWriteBusy[item[ParamField.mbusAddress]];
  });
  return res;
});

const loadingToggles = computed(() => {
  let res = false;
  booleanConfigItems.value.forEach((item) => {
    res =
      res ||
      modbusStore.modbusAddrReadBusy[item[ParamField.mbusAddress]] ||
      modbusStore.modbusAddrWriteBusy[item[ParamField.mbusAddress]];
  });
  return res;
});

const isDisabled = computed(
  () => modbusStore.portState === PortState.disconnected,
);

function reloadVariables() {
  variableConfigItems.value.forEach((item) => {
    if (
      modbusStore.storeInit &&
      modbusStore.portState === PortState.connected
    ) {
      //-------- Device Info -------------------------
      modbusStore.modbusRead(
        modbusStore.deviceAddress,
        parseInt(item[ParamField.mbusAddress]),
        modbusStore.modbusAddrLength[parseInt(item[ParamField.mbusAddress])],
      );
    }
  });
}

function reloadToggles() {
  booleanConfigItems.value.forEach((item) => {
    if (
      modbusStore.storeInit &&
      modbusStore.portState === PortState.connected
    ) {
      //-------- Device Info -------------------------
      modbusStore.modbusRead(
        modbusStore.deviceAddress,
        parseInt(item[ParamField.mbusAddress]),
        modbusStore.modbusAddrLength[parseInt(item[ParamField.mbusAddress])],
      );
    }
  });
}

watch(
  (): [string, string[]] => [activeGroupId.value, modbusStore.groups],
  (newTuple) => {
    const [newActiveGroupId, newGroups] = newTuple;
    subgroup_key.value = base_subgroup_key + Date.now().toString();

    // ---------- Check group
    // is this group found?
    const validActiveGroupId = newGroups.includes(newActiveGroupId)
      ? newActiveGroupId
      : newGroups[0];
    activeGroupId.value = validActiveGroupId;
    modbusStore.previousGroup = validActiveGroupId;

    // ---------- Check subgroup
    if (!(validActiveGroupId in modbusStore.subgroups)) {
      // this group has no subgroups
      activeSubgroupId.value = "";
    } else if (
      Object.values(modbusStore.subgroups[validActiveGroupId]).includes(
        activeSubgroupId.value,
      )
    ) {
      // this group has subgroups and the current subgroup was found among those
      modbusStore.previousSubgroup = activeSubgroupId.value;
    } else {
      // this group has subgroups but the current subgroup was not found among those
      activeSubgroupId.value = modbusStore.subgroups[validActiveGroupId][0];
    }
  },
);

watch(activeSubgroupId, (newActiveSubgroupId) => {
  modbusStore.previousSubgroup = newActiveSubgroupId;
});

watch(
  () => modbusStore.portState,
  (newPortState) => {
    if (newPortState === PortState.connected) {
      container_key.value = "container_" + new Date().getTime();
    }
  },
);

onMounted(() => {
  const route_url = route.params.group;
  switch (route_url.length) {
    case 2:
      activeGroupId.value = route_url[0];
      activeSubgroupId.value = route_url[1];
      break;
    case 1:
      activeGroupId.value = route_url[0];
      activeSubgroupId.value = "";
      break;
    case 0:
      if (modbusStore.previousGroup) {
        activeGroupId.value = modbusStore.previousGroup;
        activeSubgroupId.value = modbusStore.previousSubgroup || "";
      } else {
        activeGroupId.value = modbusStore.groups[0]; // may not be present yet
        activeSubgroupId.value = "";
      }
      break;
    default:
  }
});
</script>
